import { useState, useEffect, useCallback } from "react"
import FilterComponent from "./FilterComponent"
import DataTable from "./DataTable"
import { PATIENTS_LIST } from "../constants/api"
import { PAGINATION, MESSAGES, FILTER_OPTIONS } from "../constants/app"
import { apiUtils, errorUtils, patientUtils } from "../utils"
import type { Patient, FilterState } from "../types"

interface HomeProps {
  userEmail: string;
  isAdmin: boolean;
  userId: string;
}

export default function Home({ userEmail, isAdmin, userId }: HomeProps) {
  // Use structured state for filters
  const [filters, setFilters] = useState<FilterState>({
    searchQuery: "",
    riskLevelFilter: FILTER_OPTIONS.ALL,
    ageGroupFilter: FILTER_OPTIONS.ALL,
    genderFilter: FILTER_OPTIONS.ALL,
  });
  
  const [patients, setPatients] = useState<Patient[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchPatients = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(
        PATIENTS_LIST, 
        apiUtils.createRequestOptions('POST', {
          page: PAGINATION.DEFAULT_PAGE,
          limit: PAGINATION.DEFAULT_LIMIT
        })
      );
      
      const result = await apiUtils.handleResponse<{data: any[]}>(response);
      
      // Map and filter patients
      const allPatients: Patient[] = (result.data || []).map((p: any) => ({
        userId: p.userId,
        personal_information: p.personal_information,
        physical_details: p.physical_details,
        contact_information: p.contact_information,
        score_summary: p.score_summary,
        vitals: p.vitals,
        health_history: p.healthQuestionnaireResponse,
        hospital_information: p.hospital_information,
        hospital_id: p.hospital_id
      }));

      // Filter patients using utility function
      const filteredPatients = patientUtils.filterByHospital(allPatients, userId, isAdmin);
      setPatients(filteredPatients);
      
    } catch (error) {
      const errorMessage = errorUtils.getErrorMessage(error);
      setError(errorMessage === 'Failed to fetch patients' ? MESSAGES.ERRORS.FETCH_PATIENTS : errorMessage);
      errorUtils.logError(error, 'Home - fetchPatients');
    } finally {
      setLoading(false);
    }
  }, [userId, isAdmin]);

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  // Optimized filter handlers using single state update
  const updateFilter = useCallback((key: keyof FilterState, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  const handleSearch = useCallback((query: string) => {
    updateFilter('searchQuery', query);
  }, [updateFilter]);

  const handleRiskLevelChange = useCallback((value: string) => {
    updateFilter('riskLevelFilter', value);
  }, [updateFilter]);

  const handleAgeGroupChange = useCallback((value: string) => {
    updateFilter('ageGroupFilter', value);
  }, [updateFilter]);

  const handleGenderChange = useCallback((value: string) => {
    updateFilter('genderFilter', value);
  }, [updateFilter]);

  // Loading component
  if (loading) {
    return (
      <div className="bg-gray-100 min-h-screen">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">{MESSAGES.LOADING.PATIENTS}</p>
          </div>
        </div>
      </div>
    );
  }

  // Error component
  if (error) {
    return (
      <div className="bg-gray-100 min-h-screen">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="text-red-500 mb-4">
              <svg className="w-12 h-12 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="font-medium">{error}</p>
            </div>
            <button
              onClick={fetchPatients}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-100">
      <FilterComponent
        onSearch={handleSearch}
        onRiskLevelChange={handleRiskLevelChange}
        onAgeGroupChange={handleAgeGroupChange}
        onGenderChange={handleGenderChange}
      />

      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <DataTable
          data={patients}
          searchQuery={filters.searchQuery}
          riskLevelFilter={filters.riskLevelFilter}
          ageGroupFilter={filters.ageGroupFilter}
          genderFilter={filters.genderFilter}
          userEmail={userEmail}
          isAdmin={isAdmin}
          userId={userId}
        />
      </div>
    </div>
  )
} 